import { useState, useEffect, useCallback } from 'react';
import themeApi from '@services/themeApi';
import branchApi from '@services/branchApi';

/**
 * 테마 관련 로직을 관리하는 커스텀 훅
 * @returns {Object} 테마 관련 상태 및 함수
 */
const useTheme = () => {
  // 테마 목록 상태
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 지점 목록 상태
  const [branches, setBranches] = useState([]);
  
  // 테마 목록 조회
  const fetchThemes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await themeApi.getAllThemes();
      
      // API 응답 데이터 변환
      const formattedThemes = data.map(theme => {
        // 날짜 포맷 변환
        const startDate = new Date(theme.startDatetime);
        const endDate = new Date(theme.endDatetime);
        
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          return `${year}${month}${day}`;
        };
        
        // 무제한 날짜 처리
        const endDateStr = endDate.getFullYear() >= 2099 ? '무제한' : formatDate(endDate);
        
        return {
          id: theme.id,
          themeName: theme.themeName,
          startDatetime: theme.startDatetime,
          endDatetime: theme.endDatetime,
          usagePeriod: `${formatDate(startDate)} - ${endDateStr}`,
          deployedBranchCount: theme.deployedBranchCount,
          totalBranchCount: theme.totalBranchCount,
          branches: theme.branches || [],
          //startDate: formatDate(startDate),
          //endDate: endDateStr,
          isActive: theme.isActive,
          createdAt: theme.createdAt,
          createdBy: theme.createdBy,
          updatedAt: theme.updatedAt,
          updatedBy: theme.updatedBy,
        };
      });
      
      setThemes(formattedThemes);
      setError(null);
    } catch (err) {
      setError('테마 목록을 가져오는데 실패했습니다.');
      console.error('테마 목록 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // 지점 목록 조회
  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchApi.getBranches();

      // API 응답 구조에 따라 데이터 추출
      const branchData = response?.data?.content || response?.content || response?.data || response || [];

      // 배열인지 확인
      if (Array.isArray(branchData)) {
        // API 응답 데이터 변환
        const formattedBranches = branchData.map(branch => ({
          id: branch.id.toString(),
          name: branch.name
        }));

        setBranches(formattedBranches);
      } else {
        console.error('지점 데이터가 배열이 아닙니다:', response);
        setBranches([]);
      }
    } catch (err) {
      console.error('지점 목록 조회 실패:', err);
      setBranches([]);
    }
  }, []);
  
  // 테마 생성
  const createTheme = useCallback(async (themeData) => {
    try {
      // 날짜 포맷 변환
      const { startDatetime, endDatetime, themeName, branchIds } = themeData;
      
      const parseDate = (dateStr, isEndDate = false) => {
        if (dateStr === '무제한' && isEndDate) {
          // 무제한은 먼 미래 날짜로 설정
          return new Date(2099, 11, 31, 23, 59, 59);
        }
        
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        
        return new Date(year, month - 1, day);
      };
      
      const requestData = {
        themeName,
        startDatetime: parseDate(startDatetime).toISOString(),
        endDatetime: parseDate(endDatetime, true).toISOString(),
        isActive: true,
        branchIds
      };
      
      const responseData = await themeApi.createTheme(requestData);
      await fetchThemes(); // 목록 갱신
      return responseData;
    } catch (err) {
      console.error('테마 생성 실패:', err);
      throw err;
    }
  }, [fetchThemes]);
  
  // 테마 수정
  const updateTheme = useCallback(async (themeId, themeData) => {
    try {
      // 날짜 포맷 변환
      const { startDatetime, endDatetime, themeName, branchIds } = themeData;

      console.warn(branchIds);
      
      const parseDate = (dateStr, isEndDate = false) => {
        if (dateStr === '무제한' && isEndDate) {
          // 무제한은 먼 미래 날짜로 설정
          return new Date(2099, 11, 31, 23, 59, 59);
        }
        
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        
        return new Date(year, month - 1, day);
      };
      
      const requestData = {
        themeName,
        startDatetime: parseDate(startDatetime).toISOString(),
        endDatetime: parseDate(endDatetime, true).toISOString(),
        isActive: true,
        branchIds
      };
      
      await themeApi.updateTheme(themeId, requestData);
      await fetchThemes(); // 목록 갱신
      return true;
    } catch (err) {
      console.error(`테마 ID ${themeId} 수정 실패:`, err);
      throw err;
    }
  }, [fetchThemes]);
  
  // 테마 삭제
  const deleteTheme = useCallback(async (themeId) => {
    try {
      await themeApi.deleteTheme(themeId);
      await fetchThemes(); // 목록 갱신
      return true;
    } catch (err) {
      console.error(`테마 ID ${themeId} 삭제 실패:`, err);
      throw err;
    }
  }, [fetchThemes]);
  
  // 테마 활성화/비활성화
  const toggleThemeActivation = useCallback(async (themeId, isActive) => {
    try {
      await themeApi.toggleThemeActivation(themeId, isActive);
      await fetchThemes(); // 목록 갱신
      return true;
    } catch (err) {
      console.error(`테마 ID ${themeId} 활성화 상태 변경 실패:`, err);
      throw err;
    }
  }, [fetchThemes]);
  
  // 처음 로드 시 데이터 가져오기
  useEffect(() => {
    fetchThemes();
    fetchBranches();
  }, [fetchThemes, fetchBranches]);
  
  return {
    themes,
    loading,
    error,
    branches,
    fetchThemes,
    fetchBranches,
    createTheme,
    updateTheme,
    deleteTheme,
    toggleThemeActivation
  };
};

export default useTheme;